import { AddonDarkModeService, EnumAddonDarkMode } from '@domains/frontend/page-addons';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';


@Injectable({
	providedIn: 'root'
})
export class ThemeService {
	private readonly PRODUCT_THEME_STORAGE_KEY: string = 'flying-ui-product';
	private readonly productTheme$: BehaviorSubject<TypeUiProductTheme>;

	constructor(
		private darkModeService: AddonDarkModeService
	) {
		this.productTheme$ = new BehaviorSubject<TypeUiProductTheme>(this.getStoredProductTheme());
		this.darkModeService.onModeChange((mode: EnumAddonDarkMode) => {
			this.setAppearanceTheme(mode);
		});
	}

	initialize(): void {
		this.applyProductTheme(this.productTheme$.value);
		this.setAppearanceTheme(this.darkModeService.getActiveMode());
	}

	//** Flying Research (blue colors) */
	setResearchTheme(): void {
		this.setProductTheme('research');
		const activeMode: EnumAddonDarkMode = this.darkModeService.getActiveMode();
		this.darkModeService.setMode(activeMode);
	}

	//** Flying Upload (orange colors) */
	setUploadTheme(): void {
		this.setProductTheme('upload');
		const activeMode: EnumAddonDarkMode = this.darkModeService.getActiveMode();
		this.darkModeService.setMode(activeMode);
	}

	getProductTheme(): TypeUiProductTheme {
		return this.productTheme$.value;
	}

	getProductThemeObservable(): Observable<TypeUiProductTheme> {
		return this.productTheme$.asObservable();
	}

	/**------------------------------------------------------
	 * Helper Function
	 */
	private setProductTheme(product: TypeUiProductTheme): void {
		this.applyProductTheme(product);
		localStorage.setItem(this.PRODUCT_THEME_STORAGE_KEY, product);
		if (this.productTheme$.value !== product) this.productTheme$.next(product);
	}

	private applyProductTheme(product: TypeUiProductTheme): void {
		document.documentElement.setAttribute('data-ui-product', product);
	}

	private getStoredProductTheme(): TypeUiProductTheme {
		const storedProduct: string = localStorage.getItem(this.PRODUCT_THEME_STORAGE_KEY);
		return storedProduct === 'upload' ? 'upload' : 'research';
	}

	private setAppearanceTheme(mode: EnumAddonDarkMode): void {
		const appearance: TypeUiAppearanceTheme = mode === EnumAddonDarkMode.Dark ? 'dark' : 'light';
		document.documentElement.setAttribute('data-bs-theme', appearance);
	}
}

export type TypeUiProductTheme = 'research' | 'upload';
type TypeUiAppearanceTheme = 'light' | 'dark';
